import { useEffect, useState } from "react";
import { isEmpty } from "lodash-es";

import { LogsPanel } from "./LogsPanel";
import { isLatestLogError } from "./statusResolvers";

import { DigitizingOrdersLogsList } from "./DigitizingOrdersLogsList";
import { Panel, WorkspaceRow } from "./digitizingOrdersLogsStyled";
import { useDigitizingOrders } from "../../hooks/digitizingorderslogs/useDigitizingOrders";
import { useDigitizingOrderLogs } from "../../hooks/digitizingorderslogs/useDigitizingOrderLogs";
import { OrdersPanel } from "./OrdersPanel";

// WHAT: the three-panel DigitizingOrdersLogs workspace (find user → pick order → view
// logs). This component owns all the data-fetching and selection state;
// the actual per-panel markup lives in DigitizingOrdersLogsList, OrdersPanel, and
// LogsPanel, so this file's job is orchestration only — reading it top to
// bottom should tell you *what* the workspace does, not *how* each panel
// renders.
export const DigitizingOrdersLogsWorkspace: React.FC = () => {
  // ── Step 1: user + add-on selection (delegated to DigitizingOrdersLogsList) ──────────
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  // const [selectedAddOn, setSelectedAddOn] = useState("");
  const managerId: string | undefined = selectedUser?.parent_user_id || undefined;
  // A user is a "sub-user" whenever the list API gave us a parent_user_id
  // for them — that's the one signal the API exposes for this, so we
  // derive it here rather than trusting any other field.
  const isSubUser = !!selectedUser?.parent_user_id;

  // ── Step 2: orders ─────────────────────────────────────────────────
  const getOwnDigitizingOrderDevices = useDigitizingOrders(selectedUser?.id);
  // const getOwnReportingDevices = useGetReportingStatisticsDevices(selectedUser?.id);

  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilterText, setOrderFilterText] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  // "Show all orders" toggle — only meaningful (and only shown) for
  // sub-users, since it controls whether we ask the API for the parent
  // account's full order list instead of just this user's own.
  // Defaults to false: first load for any user, sub-user or not, should
  // only show their own orders unless the rep explicitly asks for more.
  const [showAllOrders, setShowAllOrders] = useState(false);

  // ── Step 3: logs ─────────────────────────────────────────────────────
  // Per confirmation: logs are always fetched via useDigitizingOrderLogs by orderId,
  // regardless of which add-on the order belongs to.
  const getDigitizingOrderLogs = useDigitizingOrderLogs();
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Reset the toggle whenever the selected user changes, so it doesn't
  // silently carry over "show all orders = true" from a previously
  // selected sub-user onto the next one.
  useEffect(() => {
    setShowAllOrders(false);
  }, [selectedUser?.id]);

  // WHAT: fetch orders for the current user/add-on/toggle combination,
  // and reset everything downstream (selected order, its logs) first.
  // WHY reset logs here too: previously only the order list and the
  // selected-order pointer were cleared on user/add-on change — the old
  // `logs` array was left sitting in state. Because Step 3 only renders
  // when `selectedOrder` is set, that stale data wasn't visible right
  // away, but it would flash back on screen the moment a order was
  // re-selected, until the new fetch resolved. Clearing it eagerly here
  // means switching users/add-ons empties Orders *and* Logs immediately,
  // with no stale-data flash in between.
  useEffect(() => {
    setSelectedOrder(null);
    setOrderFilterText("");
    setLogs([]);

    if (!selectedUser) {
      setOrders([]);
      return;
    }

    let cancelled = false;
    setOrdersLoading(true);

    (async () => {
      try {
        const ownFn = getOwnDigitizingOrderDevices

        // showAllOrders maps directly to the API's all_user_orders
        // flag — false (default) returns just this user's own orders,
        // true returns the full set for the account (only relevant/exposed
        // for sub-users, see the checkbox in OrdersPanel).
        const ownResponse = await ownFn(showAllOrders);
        const ownList = (ownResponse?.list ?? []).map((m: any) => ({ ...m, _owner: "own" }));
        debugger;
        if (!cancelled) setOrders(ownList);
      } catch (err) {
        // TODO: surface this to the rep via a toast/message component —
        // right now a failed fetch just quietly shows an empty order list.
        console.error("Failed to fetch orders", err);
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-run when the user/add-on/toggle actually changes — the fetch
    // functions close over selectedUser?.id/managerId each render, so
    // including them here would cause redundant re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id, managerId, showAllOrders]);

  const visibleOrders = isEmpty(orderFilterText)
    ? orders
    : orders.filter(
      (m) =>
        m.design_name?.toLowerCase().includes(orderFilterText.toLowerCase()) ||
        m.id?.toLowerCase().includes(orderFilterText.toLowerCase())
    );

  const loadLogsForOrder = async (orderId: string) => {
    setLogsLoading(true);
    setStatusFilter(undefined);
    try {
      const response = await getDigitizingOrderLogs(orderId);
      const logsList = response?.history ?? [];
      setLogs(logsList);

      // Reflect the latest log's outcome back onto this order's status
      // dot in the sidebar: if the newest log entry (top row of the table)
      // is an error, force that order's dot red by flipping
      // last_scan_status to false. We only ever move it to false here —
      // we never flip it back to true off log data, since the orders
      // list API's own last_scan_status is the source of truth for the
      // "no error" case.
      // if (logsList.length > 0) {
      //   const hasLatestError = isLatestLogError(logsList);
      //   setOrders((prev) =>
      //     prev.map((m) =>
      //       m.order_id === orderId ? { ...m, last_scan_status: !hasLatestError } : m
      //     )
      //   );
      // }
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleOrderSelect = (order: any, orderId: string) => {
    setSelectedOrder(order);
    setStatusFilter(undefined);
    loadLogsForOrder(orderId);
  };

  const filteredLogs = logs.filter((log) => {
    if (statusFilter && log?.event_type?.toString().toLowerCase() !== statusFilter) return false;
    return true;
  });

  return (
    <WorkspaceRow>
      {/* STEP 1 — Find user */}
      <Panel width={260}>

        <DigitizingOrdersLogsList
          onSelectionChange={(user) => {
            setSelectedUser(user);
            // setSelectedAddOn(addOn);
          }}
        />


      </Panel>

      {/* STEP 2 — Orders */}
      <Panel width={260}>

        <OrdersPanel
          selectedUser={selectedUser}

          isSubUser={isSubUser}
          showAllOrders={showAllOrders}
          onShowAllOrdersChange={setShowAllOrders}
          orderFilterText={orderFilterText}
          onOrderFilterTextChange={setOrderFilterText}
          ordersLoading={ordersLoading}
          visibleOrders={visibleOrders}
          selectedOrder={selectedOrder}
          onOrderSelect={handleOrderSelect}
        />

      </Panel>

      {/* STEP 3 — Logs */}
      <Panel>
        <LogsPanel
          selectedUser={selectedUser}
          selectedOrder={selectedOrder}
          filteredLogs={filteredLogs}
          logsLoading={logsLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={() => selectedOrder && loadLogsForOrder(selectedOrder.id)}
        />

      </Panel>
    </WorkspaceRow>
  );
};
